import { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import {
  FreshStrawTripMeta,
  useGetFreshStrawTripMetaQuery,
  useUpdateFreshStrawTripMetaMutation,
} from '@/app/services/freshStrawRest';
import { useGetKickOutCommentsQuery } from '@/app/services/inClinic';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button } from '@/components/ui/button';
import { KickOutCommentPicker } from '@/pages/in-clinic/components/KickOutCommentPicker';

interface FreshStrawTripMetaPanelProps {
  stopId: number;
}

type DraftMeta = {
  kickOuts: string;
  comments: string;
  mileage: string;
  priceTier: string;
  tripComment: string;
};

function toDraft(meta: FreshStrawTripMeta | null | undefined): DraftMeta {
  return {
    kickOuts: meta?.kickOuts ?? '',
    comments: meta?.comments ?? '',
    mileage: meta?.mileage != null ? String(meta.mileage) : '',
    priceTier: meta?.priceTier ?? '',
    tripComment: meta?.tripComment ?? '',
  };
}

export function FreshStrawTripMetaPanel({
  stopId,
}: FreshStrawTripMetaPanelProps) {
  const dispatch = useDispatch();
  const { data, isFetching, isError } = useGetFreshStrawTripMetaQuery(
    { stopId },
    { skip: !stopId || stopId < 1 },
  );
  const [update, updateState] = useUpdateFreshStrawTripMetaMutation();
  const { data: kickOutComments = [] } = useGetKickOutCommentsQuery();

  const [draft, setDraft] = useState<DraftMeta>(toDraft(data));

  const appendKickOut = (comment: string) => {
    setDraft((d) => {
      const existing = d.kickOuts.trim();
      if (!existing) return { ...d, kickOuts: comment };
      const parts = existing.split(/\s*;\s*/).filter(Boolean);
      if (parts.includes(comment)) return d;
      return { ...d, kickOuts: `${existing}; ${comment}` };
    });
  };

  useEffect(() => {
    setDraft(toDraft(data));
  }, [data]);

  const handleSave = async () => {
    try {
      await update({
        stopId,
        kickOuts: draft.kickOuts,
        comments: draft.comments,
        mileage: draft.mileage === '' ? null : Number(draft.mileage),
        priceTier: draft.priceTier,
        tripComment: draft.tripComment,
      }).unwrap();
      dispatch(
        addToast({
          severity: 'success',
          summary: 'Trip saved',
          detail: 'Kick-outs, comments, and price tier updated.',
          life: ToastLife.short,
        }),
      );
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'data' in err
          ? ((err as { data?: { message?: string } }).data?.message ??
            'Save failed.')
          : 'Save failed.';
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Could not save trip',
          detail: message,
          life: ToastLife.long,
        }),
      );
    }
  };

  if (isError) {
    return (
      <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        Could not load trip metadata for stop {stopId}.
      </div>
    );
  }

  return (
    <details className="rounded border border-gray-200 bg-white" open>
      <summary className="cursor-pointer px-3 py-2 text-sm font-semibold text-gray-700">
        Trip metadata
        {data?.tripTankName && (
          <span className="ml-2 text-xs font-normal text-gray-500">
            · Tank: {data.tripTankName}
          </span>
        )}
      </summary>
      <div className="grid gap-3 border-t border-gray-200 p-3 text-sm md:grid-cols-2 lg:grid-cols-3">
        <Field label="Kick-outs" className="md:col-span-2 lg:col-span-3">
          <textarea
            value={draft.kickOuts}
            onChange={(e) =>
              setDraft((d) => ({ ...d, kickOuts: e.target.value }))
            }
            rows={2}
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
          />
          <KickOutCommentPicker
            comments={kickOutComments}
            description={draft.kickOuts}
            onSelect={appendKickOut}
          />
        </Field>
        <Field label="Mileage">
          <input
            type="number"
            value={draft.mileage}
            onChange={(e) =>
              setDraft((d) => ({ ...d, mileage: e.target.value }))
            }
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
          />
        </Field>
        <Field label="Price tier">
          <input
            type="text"
            value={draft.priceTier}
            onChange={(e) =>
              setDraft((d) => ({ ...d, priceTier: e.target.value }))
            }
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
          />
        </Field>
        <Field label="Stop comments" className="md:col-span-2">
          <textarea
            value={draft.comments}
            onChange={(e) =>
              setDraft((d) => ({ ...d, comments: e.target.value }))
            }
            rows={2}
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
          />
        </Field>
        <Field
          label="Trip comment (shipment)"
          className="md:col-span-2 lg:col-span-3"
        >
          <textarea
            value={draft.tripComment}
            onChange={(e) =>
              setDraft((d) => ({ ...d, tripComment: e.target.value }))
            }
            rows={2}
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
          />
        </Field>
      </div>
      <div className="flex items-center gap-3 border-t border-gray-200 px-3 py-2">
        <Button
          onClick={handleSave}
          disabled={isFetching || updateState.isLoading}
        >
          {updateState.isLoading ? 'Saving…' : 'Save trip metadata'}
        </Button>
        {isFetching && <span className="text-xs text-gray-500">Loading…</span>}
      </div>
    </details>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`flex flex-col gap-1 ${className ?? ''}`}>
      <span className="text-xs font-medium text-gray-600">{label}</span>
      {children}
    </label>
  );
}
